import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  createStockTransferApi,
  acceptStockTransferApi,
  fetchPendingIncomingTransfersApi,
  fetchPendingOutgoingTransfersApi,
  cancelStockTransferApi,
  fetchIncomingSerialsApi,
  fetchOutgoingSerialsApi,
  fetchAvailableProductionUnitsApi,
} from "../../../api/StockTransfer/stockTransfer.api";
import ResponseModel from "../../../models/response.model";
import { IProductionUnit } from "../../../models/factory/productionUnit.model";

// --- Types ---
export interface SerialData {
  _id: string;
  serialNumber: string;
}

export interface TransferItemInput {
  serialNumber: string;
  senderRemark?: string;
}

export interface CreateTransferPayload {
  fromWarehouse?: string;
  toWarehouse: string;
  transferCost: number;
  handlingCost: number;
  items: TransferItemInput[];
}

export interface StockTransfer {
  _id: string;
  fromWarehouse: { _id: string; name?: string };
  toWarehouse: { _id: string; name?: string };
  sentBy: { _id: string; username?: string };
  serialNumber: string;
  productName?: string;
  senderRemark?: string;
  sentDate: string;
}

export interface PaginatedResponse {
  transfers: StockTransfer[];
  total: number;
  page: number;
  totalPages: number;
}

interface StockTransferState {
  incoming: {
    items: StockTransfer[];
    page: number;
    total: number;
    totalPages: number;
    loading: boolean;
  };
  outgoing: {
    items: StockTransfer[];
    page: number;
    total: number;
    totalPages: number;
    loading: boolean;
  };
  incomingSerials: SerialData[];
  outgoingSerials: SerialData[];
  actionLoading: boolean;
  availableProductionUnits: IProductionUnit[];
  availableProductionUnitsLoading: boolean;
  error: string | null;
}

const initialState: StockTransferState = {
  incoming: { items: [], page: 1, total: 0, totalPages: 0, loading: false },
  outgoing: { items: [], page: 1, total: 0, totalPages: 0, loading: false },
  incomingSerials: [],
  outgoingSerials: [],
  actionLoading: false,
  availableProductionUnits: [],
  availableProductionUnitsLoading: false,
  error: null,
};

// --- Thunks ---

export const createTransfer = createAsyncThunk(
  "stockTransfer/create",
  async (payload: CreateTransferPayload, { rejectWithValue }) => {
    try {
      return await createStockTransferApi(payload);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to create transfers",
      );
    }
  },
);

// Updated to accept Array
export const acceptTransfer = createAsyncThunk(
  "stockTransfer/accept",
  async (
    items: Array<{ transferId: string; remark?: string }>,
    { rejectWithValue },
  ) => {
    try {
      await acceptStockTransferApi(items);
      return items;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to accept transfers",
      );
    }
  },
);

export const cancelTransfer = createAsyncThunk(
  "stockTransfer/cancel",
  async (
    items: Array<{ transferId: string; remark?: string }>,
    { rejectWithValue },
  ) => {
    try {
      await cancelStockTransferApi(items);
      return items;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to cancel transfers",
      );
    }
  },
);

export const fetchPendingIncomingTransfers = createAsyncThunk(
  "stockTransfer/fetchIncoming",
  async (page: number = 1, { rejectWithValue }) => {
    try {
      return await fetchPendingIncomingTransfersApi(page, 10);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load incoming",
      );
    }
  },
);

export const fetchPendingOutgoingTransfers = createAsyncThunk(
  "stockTransfer/fetchOutgoing",
  async (page: number = 1, { rejectWithValue }) => {
    try {
      return await fetchPendingOutgoingTransfersApi(page, 10);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load outgoing",
      );
    }
  },
);

export const fetchIncomingSerials = createAsyncThunk(
  "stockTransfer/fetchIncomingSerials",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetchIncomingSerialsApi();
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  },
);

export const fetchOutgoingSerials = createAsyncThunk(
  "stockTransfer/fetchOutgoingSerials",
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetchOutgoingSerialsApi();
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  },
);

export const fetchAvailableProductionUnits = createAsyncThunk<
  ResponseModel,
  {
    params: {
      page: number;
      limit: number;
      search?: string;
    };
  },
  { rejectValue: string }
>(
  "stockTransfer/fetchAvailableProductionUnits",
  async ({ params }, { rejectWithValue }) => {
    try {
      return await fetchAvailableProductionUnitsApi(params);
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || "Failed");
    }
  },
);

// --- Slice ---

const stockTransferSlice = createSlice({
  name: "stockTransfer",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createTransfer.pending, (s) => {
        s.actionLoading = true;
        s.error = null;
      })
      .addCase(createTransfer.fulfilled, (s) => {
        s.actionLoading = false;
      })
      .addCase(createTransfer.rejected, (s, a) => {
        s.actionLoading = false;
        s.error = a.payload as string;
      })

      // Accept (Bulk)
      .addCase(acceptTransfer.pending, (s) => {
        s.actionLoading = true;
      })
      .addCase(acceptTransfer.fulfilled, (s, action) => {
        s.actionLoading = false;
        const acceptedIds = new Set(action.payload.map((i) => i.transferId));
        s.incoming.items = s.incoming.items.filter(
          (t) => !acceptedIds.has(t._id),
        );
        s.incoming.total -= acceptedIds.size;
      })
      .addCase(acceptTransfer.rejected, (s, a) => {
        s.actionLoading = false;
        s.error = a.payload as string;
      })

      // Cancel (Bulk)
      .addCase(cancelTransfer.pending, (s) => {
        s.actionLoading = true;
      })
      .addCase(cancelTransfer.fulfilled, (s, action) => {
        s.actionLoading = false;
        const cancelledIds = new Set(action.payload.map((i) => i.transferId));
        s.outgoing.items = s.outgoing.items.filter(
          (t) => !cancelledIds.has(t._id),
        );
        s.outgoing.total -= cancelledIds.size;
      })
      .addCase(cancelTransfer.rejected, (s, a) => {
        s.actionLoading = false;
        s.error = a.payload as string;
      })

      // Incoming Fetch
      .addCase(fetchPendingIncomingTransfers.pending, (s) => {
        s.incoming.loading = true;
      })
      .addCase(
        fetchPendingIncomingTransfers.fulfilled,
        (s, a: PayloadAction<PaginatedResponse>) => {
          s.incoming.loading = false;
          s.incoming.items = a.payload.transfers;
          s.incoming.page = a.payload.page;
          s.incoming.total = a.payload.total;
          s.incoming.totalPages = a.payload.totalPages;
        },
      )
      .addCase(fetchPendingIncomingTransfers.rejected, (s, a) => {
        s.incoming.loading = false;
        s.error = a.payload as string;
      })

      // Outgoing Fetch
      .addCase(fetchPendingOutgoingTransfers.pending, (s) => {
        s.outgoing.loading = true;
      })
      .addCase(
        fetchPendingOutgoingTransfers.fulfilled,
        (s, a: PayloadAction<PaginatedResponse>) => {
          s.outgoing.loading = false;
          s.outgoing.items = a.payload.transfers;
          s.outgoing.page = a.payload.page;
          s.outgoing.total = a.payload.total;
          s.outgoing.totalPages = a.payload.totalPages;
        },
      )
      .addCase(fetchPendingOutgoingTransfers.rejected, (s, a) => {
        s.outgoing.loading = false;
        s.error = a.payload as string;
      })
      .addCase(fetchIncomingSerials.fulfilled, (s, a) => {
        s.incomingSerials = a.payload;
      })
      .addCase(fetchOutgoingSerials.fulfilled, (s, a) => {
        s.outgoingSerials = a.payload;
      })
      .addCase(fetchAvailableProductionUnits.pending, (s) => {
        s.availableProductionUnitsLoading = true;
      })
      .addCase(fetchAvailableProductionUnits.fulfilled, (s, a) => {
        s.availableProductionUnitsLoading = false;
        s.availableProductionUnits = a.payload.data;
      })
      .addCase(fetchAvailableProductionUnits.rejected, (s, a) => {
        s.availableProductionUnitsLoading = false;
        s.error = a.payload as string;
      });
  },
});

export const { clearError } = stockTransferSlice.actions;
export default stockTransferSlice.reducer;
